import { useCallback, useMemo, useRef } from 'react'
import { normalizeToMinute } from '../utils/datetime'
import { useTaskReminders } from './useTaskReminders'
import type { DateDurationPickerProps } from '../type'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import type { RecurrencePattern } from '@/lib/types/recurrence'
import { useReminderMutations, useTaskMutations } from '@/lib/db/rxdb/hooks'
import { logger } from '@/lib/monitoring/logger'
import useLoginStore from '@/store/LoginStore'

type ScheduleProps = Pick<
  DateDurationPickerProps,
  | 'startDate'
  | 'dueDate'
  | 'onStartDateChange'
  | 'onDueDateChange'
  | 'isSomeday'
  | 'onSomedayChange'
  | 'isAllDay'
  | 'onIsAllDayChange'
  | 'reminderOffsetMinutes'
  | 'onReminderChange'
  | 'recurrencePattern'
  | 'onRecurrenceChange'
  | 'taskCreatedOn'
>

/**
 * Everything a DateDurationPicker needs to edit one saved task in place: dates,
 * someday, all-day, and the primary reminder with its recurrence. The same save
 * rules as the task detail, without its optimistic local state, so a task row can
 * open the full picker instead of a short preset list.
 */
export function useTaskScheduleEditor(task: TaskDocument): {
  props: ScheduleProps
  isLoading: boolean
} {
  const workspaceId = useLoginStore((state) => state.workspaceId)
  const userId = useLoginStore((state) => state.userId)
  const { updateTask } = useTaskMutations()
  const { createReminder, updateReminder, deleteReminder } = useReminderMutations()
  const { primaryReminder, isLoading } = useTaskReminders(task.id, workspaceId)

  const recurrencePattern = useMemo<RecurrencePattern | null>(() => {
    if (!primaryReminder || primaryReminder.recurrenceType === null) return null
    return {
      recurrenceType: primaryReminder.recurrenceType,
      recurrenceInterval: primaryReminder.recurrenceInterval,
      recurrenceDaysOfWeek: primaryReminder.recurrenceDaysOfWeek,
      recurrenceDayOfMonth: primaryReminder.recurrenceDayOfMonth,
      recurrenceEndDate: primaryReminder.recurrenceEndDate
        ? (new Date(primaryReminder.recurrenceEndDate).toISOString().split('T')[0] ?? null)
        : null,
      recurrenceMaxOccurrences: primaryReminder.recurrenceMaxOccurrences,
    }
  }, [primaryReminder])

  // A repeat picked before any reminder exists is saved with the reminder.
  const pendingRecurrenceRef = useRef<RecurrencePattern | null>(null)

  const save = useCallback(
    (updates: Partial<TaskDocument>, field: string) =>
      updateTask(task.id, updates).catch((error: unknown) => {
        logger.logError(error as Error, 'TASK_UPDATE', { taskId: task.id, field })
      }),
    [task.id, updateTask],
  )

  const onDueDateChange = useCallback(
    (date: Date | undefined) => {
      const next = date ? normalizeToMinute(date).getTime() : null
      const current = task.dueDate ? normalizeToMinute(new Date(task.dueDate)).getTime() : null
      if (next === current) return
      void save({ dueDate: next, dueDateType: next === null ? null : 'datetime' }, 'dueDate')
    },
    [task.dueDate, save],
  )

  const onStartDateChange = useCallback(
    (date: Date | undefined) => {
      const next = date ? normalizeToMinute(date).getTime() : null
      const current = task.startDate ? normalizeToMinute(new Date(task.startDate)).getTime() : null
      if (next === current) return
      void save({ startDate: next, startDateType: next === null ? null : 'datetime' }, 'startDate')
    },
    [task.startDate, save],
  )

  const onIsAllDayChange = useCallback(
    (value: boolean) => {
      if (task.isAllDay === value) return
      void save({ isAllDay: value }, 'isAllDay')
    },
    [task.isAllDay, save],
  )

  const onSomedayChange = useCallback(
    (value: boolean) => {
      if (task.isSomeday === value) return
      if (!value) {
        void save({ isSomeday: false }, 'isSomeday')
        return
      }
      // Someday clears every scheduling field, the reminder included.
      void save(
        { isSomeday: true, startDate: null, startDateType: null, dueDate: null, dueDateType: null },
        'isSomeday',
      )
      if (primaryReminder) void deleteReminder(primaryReminder.id)
    },
    [task.isSomeday, primaryReminder, save, deleteReminder],
  )

  const onRecurrenceChange = useCallback(
    (pattern: RecurrencePattern | null) => {
      pendingRecurrenceRef.current = pattern
      if (!primaryReminder) return
      updateReminder(primaryReminder.id, {
        recurrenceType: pattern?.recurrenceType ?? null,
        recurrenceInterval: pattern?.recurrenceInterval ?? null,
        recurrenceDaysOfWeek: pattern?.recurrenceDaysOfWeek ?? null,
        recurrenceDayOfMonth: pattern?.recurrenceDayOfMonth ?? null,
        recurrenceEndDate: pattern?.recurrenceEndDate ? new Date(pattern.recurrenceEndDate).getTime() : null,
        recurrenceMaxOccurrences: pattern?.recurrenceMaxOccurrences ?? null,
      }).catch((error: unknown) => {
        logger.logError(error as Error, 'REMINDER_UPDATE', { reminderId: primaryReminder.id, taskId: task.id })
      })
    },
    [primaryReminder, updateReminder, task.id],
  )

  const onReminderChange = useCallback(
    async (
      offsetMinutes: number | null,
      nextScheduledAt: number | null,
      pendingRecurrence?: RecurrencePattern | null,
    ) => {
      if (!userId || !workspaceId) return
      const recurrence = pendingRecurrence !== undefined ? pendingRecurrence : pendingRecurrenceRef.current
      try {
        if (offsetMinutes === null) {
          if (primaryReminder) await deleteReminder(primaryReminder.id)
        } else if (primaryReminder) {
          if (
            offsetMinutes !== primaryReminder.relativeOffsetMinutes ||
            nextScheduledAt !== primaryReminder.nextScheduledAt
          ) {
            await updateReminder(primaryReminder.id, { relativeOffsetMinutes: offsetMinutes, nextScheduledAt })
          }
        } else {
          await createReminder({
            id: crypto.randomUUID(),
            taskId: task.id,
            reminderType: 1, // 1 = Relative to due_date
            absoluteDate: null,
            relativeOffsetMinutes: offsetMinutes,
            notifyUserId: userId,
            notificationChannel: 'app',
            isEnabled: true,
            isSent: false,
            sentAt: null,
            recurrenceType: recurrence?.recurrenceType ?? null,
            recurrenceInterval: recurrence?.recurrenceInterval ?? null,
            recurrenceDaysOfWeek: recurrence?.recurrenceDaysOfWeek ?? null,
            recurrenceDayOfMonth: recurrence?.recurrenceDayOfMonth ?? null,
            recurrenceEndDate: recurrence?.recurrenceEndDate
              ? new Date(recurrence.recurrenceEndDate).getTime()
              : null,
            recurrenceMaxOccurrences: recurrence?.recurrenceMaxOccurrences ?? null,
            occurrenceCount: 0,
            lastSentAt: null,
            nextScheduledAt,
          })
        }
      } catch (error) {
        logger.logError(error instanceof Error ? error : new Error(String(error)), 'DATABASE', {
          operation: 'taskRowReminder',
          taskId: task.id,
        })
      }
    },
    [userId, workspaceId, primaryReminder, task.id, createReminder, updateReminder, deleteReminder],
  )

  return {
    isLoading,
    props: {
      startDate: task.startDate ? new Date(task.startDate) : null,
      dueDate: task.dueDate ? new Date(task.dueDate) : null,
      isSomeday: task.isSomeday,
      isAllDay: task.isAllDay,
      reminderOffsetMinutes: primaryReminder?.relativeOffsetMinutes ?? null,
      recurrencePattern,
      taskCreatedOn: task.createdOn,
      onStartDateChange,
      onDueDateChange,
      onIsAllDayChange,
      onSomedayChange,
      onReminderChange: (offset, next, pending) => void onReminderChange(offset, next, pending),
      onRecurrenceChange,
    },
  }
}
